import { isSupabaseConfigured, supabase } from '../../lib/supabase'
import { activeTenant } from '../../lib/tenant'

export type CommercialRequestPayload = {
  projectId: string; projectUnitId: string; firstName: string; lastName: string
  documentType: 'cedula' | 'passport' | 'other'; documentNumber: string
  nationality?: string; maritalStatus?: 'single' | 'married' | 'divorced' | 'widowed' | 'other'
  email: string; phone: string; whatsapp?: string; city?: string; address?: string
  employmentStatus?: string; occupationCompany?: string; incomeRange?: string
  fundsSource?: string; brokerReference?: string; consent: boolean
}

export type CommercialRequestResult = { id: string; caseCode: string; accessToken: string; emailSent: boolean }
export type CommercialCaseStatus = {
  case_code: string; status: string; project_name: string | null; unit_reference: string | null
  unit_status: string | null; plan_id: string | null; plan_status: string | null
  reservation_amount: number | null; currency: 'USD' | 'DOP' | null
  can_upload_proof: boolean; proof_status: string | null; proof_submitted_at: string | null
  proof_review_notes: string | null
}

export type PortalPaymentStage = {
  id: string; stage_type: string; label: string; amount: number; amount_paid: number
  balance: number; status: string; sequence_number: number
}
export type PortalPaymentInstallment = {
  id: string; stage_id: string | null; installment_number: number; label: string | null
  due_date: string; amount_due: number; amount_paid: number; balance: number; status: string
}
export type ClientPortalPaymentOptions = {
  client_id: string; client_code: string | null; client_name: string
  plan: { id: string; plan_number: string; currency: 'USD' | 'DOP'; sale_price: number; unit_reference: string | null; status: string } | null
  stages: PortalPaymentStage[]; installments: PortalPaymentInstallment[]
}

const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
const createAccessToken = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), value => alphabet[value % alphabet.length]).join('')
const sha256 = async (value: string) => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))), byte => byte.toString(16).padStart(2, '0')).join('')
const optional = (value?: string) => value?.trim() || null
const requireSupabase = () => {
  if (!isSupabaseConfigured || !supabase) throw new Error('La conexión segura con SIGA no está configurada.')
  return supabase
}
const normalizeAccess = (value: string) => value.trim().toUpperCase()

export async function submitCommercialRequest(input: CommercialRequestPayload): Promise<CommercialRequestResult> {
  const client = requireSupabase()
  const id = crypto.randomUUID()
  const caseCode = `WEB-${id.replaceAll('-', '').slice(0, 10).toUpperCase()}`
  const accessToken = createAccessToken()
  const portalAccessHash = await sha256(accessToken)
  const { error } = await client.from('webapp_commercial_requests').insert({
    id, project_id: input.projectId, project_unit_id: input.projectUnitId,
    first_name: input.firstName.trim(), last_name: input.lastName.trim(),
    document_type: input.documentType, document_number: input.documentNumber.trim(),
    nationality: optional(input.nationality), marital_status: optional(input.maritalStatus),
    email: input.email.trim().toLowerCase(), phone: input.phone.trim(), whatsapp: optional(input.whatsapp),
    city: optional(input.city), address: optional(input.address), employment_status: optional(input.employmentStatus),
    occupation_company: optional(input.occupationCompany), income_range: optional(input.incomeRange),
    funds_source: optional(input.fundsSource), broker_reference: optional(input.brokerReference),
    consent: input.consent, portal_access_hash: portalAccessHash,
  })
  if (error) throw error
  try { localStorage.setItem(`webapp-${activeTenant.code.toLowerCase()}-commercial-case`, JSON.stringify({ caseCode, accessToken })) } catch { /* ignored */ }
  const { error: emailError } = await client.functions.invoke('send-webapp-case-access', { body: { caseCode, accessToken } })
  return { id, caseCode, accessToken, emailSent: !emailError }
}

export async function loadCommercialCase(caseCode: string, accessToken: string): Promise<CommercialCaseStatus> {
  const client = requireSupabase()
  const params = { p_case_code: normalizeAccess(caseCode), p_access_token: normalizeAccess(accessToken) }
  const { data, error } = await client.rpc('get_webapp_case_status', params)
  if (error) throw error
  void client.functions.invoke('send-webapp-case-access', { body: { caseCode: params.p_case_code, accessToken: params.p_access_token } })
  return data as CommercialCaseStatus
}

export async function uploadReservationProof(input: { caseCode: string; accessToken: string; file: File; amount: number; currency: 'USD' | 'DOP'; reference: string }) {
  const client = requireSupabase()
  if (!['image/jpeg','image/png','application/pdf'].includes(input.file.type)) throw new Error('Usa un archivo JPG, PNG o PDF.')
  if (input.file.size > 10 * 1024 * 1024) throw new Error('El archivo no puede superar 10 MB.')
  const caseCode = normalizeAccess(input.caseCode); const accessToken = normalizeAccess(input.accessToken)
  const safeName = input.file.name.replace(/[^A-Za-z0-9._-]/g, '_')
  const credentialHash = await sha256(accessToken)
  const path = `${caseCode}/${credentialHash}/${crypto.randomUUID()}-${safeName}`
  const { error: uploadError } = await client.storage.from('reservation-proofs').upload(path, input.file, { contentType: input.file.type, upsert: false })
  if (uploadError) throw uploadError
  const { data, error } = await client.rpc('submit_webapp_reservation_proof', {
    p_case_code: caseCode, p_access_token: accessToken, p_storage_path: path,
    p_original_name: input.file.name, p_mime_type: input.file.type,
    p_amount: input.amount, p_currency: input.currency, p_reference: input.reference.trim(),
  })
  if (error) throw error
  return data
}

export async function loadClientPortalPayments(caseCode: string, accessToken: string): Promise<ClientPortalPaymentOptions> {
  const client = requireSupabase()
  const { data, error } = await client.rpc('get_client_portal_payment_options', {
    p_case_code: normalizeAccess(caseCode), p_access_token: normalizeAccess(accessToken),
  })
  if (error) throw error
  return data as ClientPortalPaymentOptions
}

export async function uploadClientPortalPaymentProof(input: {
  caseCode: string; accessToken: string; planId: string; stageId?: string | null
  installmentId?: string | null; file: File; amount: number; currency: 'USD' | 'DOP'
  paymentDate: string; reference: string
}) {
  const client = requireSupabase()
  if (!input.stageId && !input.installmentId) throw new Error('Selecciona una etapa o cuota.')
  if (!['image/jpeg','image/png','application/pdf'].includes(input.file.type)) throw new Error('Usa un archivo JPG, PNG o PDF.')
  if (input.file.size > 10 * 1024 * 1024) throw new Error('El archivo no puede superar 10 MB.')
  if (!Number.isFinite(input.amount) || input.amount <= 0) throw new Error('Indica un monto válido.')
  const caseCode = normalizeAccess(input.caseCode); const accessToken = normalizeAccess(input.accessToken)
  const safeName = input.file.name.replace(/[^A-Za-z0-9._-]/g, '_')
  const credentialHash = await sha256(accessToken)
  const path = `portal/${caseCode}/${credentialHash}/${crypto.randomUUID()}-${safeName}`
  const { error: uploadError } = await client.storage.from('client-payment-proofs').upload(path, input.file, { contentType: input.file.type, upsert: false })
  if (uploadError) throw uploadError
  const { data, error } = await client.rpc('submit_client_portal_payment_proof', {
    p_case_code: caseCode, p_access_token: accessToken, p_plan_id: input.planId,
    p_stage_id: input.stageId || null, p_installment_id: input.installmentId || null,
    p_storage_path: path, p_original_name: input.file.name, p_mime_type: input.file.type,
    p_amount: input.amount, p_currency: input.currency, p_payment_date: input.paymentDate,
    p_reference: input.reference.trim(),
  })
  if (error) throw error
  return data as { submission_id: string; status: 'pending' }
}

export async function uploadClientPortalPaymentProofByClient(input: {
  clientCode: string; accessToken: string; planId: string; stageId?: string | null
  installmentId?: string | null; file: File; amount: number; currency: 'USD' | 'DOP'
  paymentDate: string; reference: string
}) {
  const client = requireSupabase()
  if (!input.stageId && !input.installmentId) throw new Error('Selecciona una etapa o cuota.')
  if (!['image/jpeg','image/png','application/pdf'].includes(input.file.type)) throw new Error('Usa un archivo JPG, PNG o PDF.')
  if (input.file.size > 10 * 1024 * 1024) throw new Error('El archivo no puede superar 10 MB.')
  if (!Number.isFinite(input.amount) || input.amount <= 0) throw new Error('Indica un monto válido.')

  const clientCode = normalizeAccess(input.clientCode)
  const accessToken = normalizeAccess(input.accessToken)
  const safeName = input.file.name.replace(/[^A-Za-z0-9._-]/g, '_')
  const credentialHash = await sha256(accessToken)
  const path = `portal/${clientCode}/${credentialHash}/${crypto.randomUUID()}-${safeName}`

  const { error: uploadError } = await client.storage
    .from('client-payment-proofs')
    .upload(path, input.file, { contentType: input.file.type, upsert: false })
  if (uploadError) throw uploadError

  const { data, error } = await client.rpc('submit_client_portal_payment_proof_by_client', {
    p_client_code: clientCode,
    p_access_token: accessToken,
    p_plan_id: input.planId,
    p_stage_id: input.stageId || null,
    p_installment_id: input.installmentId || null,
    p_storage_path: path,
    p_original_name: input.file.name,
    p_mime_type: input.file.type,
    p_amount: input.amount,
    p_currency: input.currency,
    p_payment_date: input.paymentDate,
    p_reference: input.reference.trim(),
  })

  if (error) {
    await client.storage.from('client-payment-proofs').remove([path])
    throw error
  }
  return data as { submission_id: string; status: 'pending' }
}
